import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  IconButton,
  CircularProgress,
  Alert,
  Tooltip,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { format, startOfWeek, endOfWeek, startOfMonth } from 'date-fns';
import apiService from '../../services/api';

interface CheckInsSummaryCardProps {
  refreshTrigger?: number;
}

interface CheckInStats {
  today: number;
  week: number;
  month: number;
  byStatus: Array<{
    _id: string;
    count: number;
  }>;
}

const CheckInsSummaryCard: React.FC<CheckInsSummaryCardProps> = ({ refreshTrigger = 0 }) => {
  const [stats, setStats] = useState<CheckInStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchCheckInStats();
  }, [refreshTrigger]);

  // Utility functions for date calculations
  const getCurrentWeekRange = () => {
    const today = new Date();
    const monday = startOfWeek(today, { weekStartsOn: 1 }); // Monday start
    const sunday = endOfWeek(today, { weekStartsOn: 1 }); // Sunday end
    return {
      startDate: format(monday, 'yyyy-MM-dd'),
      endDate: format(sunday, 'yyyy-MM-dd'),
    };
  };

  const getCurrentMonthRange = () => {
    const today = new Date();
    const monthStart = startOfMonth(today);
    return {
      startDate: format(monthStart, 'yyyy-MM-dd'),
      endDate: format(today, 'yyyy-MM-dd'), // Up to today
    };
  };

  const getTodayRange = () => {
    const today = new Date();
    return {
      startDate: format(today, 'yyyy-MM-dd'),
      endDate: format(today, 'yyyy-MM-dd'),
    };
  };

  const handleWeeklyClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent card click
    const { startDate, endDate } = getCurrentWeekRange();
    navigate(`/daily-checkins?startDate=${startDate}&endDate=${endDate}`);
  };

  const handleMonthlyClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent card click
    const { startDate, endDate } = getCurrentMonthRange();
    navigate(`/daily-checkins?startDate=${startDate}&endDate=${endDate}`);
  };

  const handleStatusClick = (status: string) => (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent card click
    const { startDate, endDate } = getTodayRange();
    navigate(`/daily-checkins?startDate=${startDate}&endDate=${endDate}&status=${status}`);
  };

  const fetchCheckInStats = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.getCheckInStats();
      setStats(response);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load check-in statistics');
      console.error('Error fetching check-in stats:', err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'success':
        return <CheckCircleIcon sx={{ fontSize: 16, color: '#4caf50' }} />;
      case 'contract-expired':
        return <ErrorIcon sx={{ fontSize: 16, color: '#f44336' }} />;
      case 'payment-overdue':
        return <WarningIcon sx={{ fontSize: 16, color: '#ff9800' }} />;
      case 'suspended':
        return <ErrorIcon sx={{ fontSize: 16, color: '#f44336' }} />;
      default:
        return <CheckCircleIcon sx={{ fontSize: 16, color: '#9aa0a6' }} />;
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'success':
        return 'Successful';
      case 'contract-expired':
        return 'Expired';
      case 'payment-overdue':
        return 'Payment Due';
      case 'suspended':
        return 'Suspended';
      default:
        return status;
    }
  };

  if (loading) {
    return (
      <Card sx={{
        background: 'linear-gradient(135deg, #fafbfc 0%, #f7fafc 100%)',
        border: '1px solid #e5e9f0',
        borderRadius: 3,
      }}>
        <CardContent>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress sx={{ color: '#1976d2' }} />
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card sx={{
        background: 'linear-gradient(135deg, #fafbfc 0%, #f7fafc 100%)',
        border: '1px solid #e5e9f0',
        borderRadius: 3,
      }}>
        <CardContent>
          <Alert severity="error" sx={{ 
            mb: 2,
            backgroundColor: 'rgba(229, 62, 62, 0.1)',
            border: '1px solid rgba(229, 62, 62, 0.3)',
            color: '#e53e3e',
            borderRadius: 2,
          }}>
            {error}
          </Alert>
        </CardContent>
      </Card>
    );
  }

  if (!stats) {
    return (
      <Card sx={{
        background: 'linear-gradient(135deg, #fafbfc 0%, #f7fafc 100%)',
        border: '1px solid #e5e9f0',
        borderRadius: 3,
      }}>
        <CardContent>
          <Typography variant="body2" sx={{ color: '#4a5568' }}>
            No check-in data available
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card 
      sx={{ 
        background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
        border: '2px solid #e2e8f0',
        borderRadius: 4,
        boxShadow: '0 10px 40px rgba(0, 0, 0, 0.08)',
        transition: 'all 0.3s ease-in-out',
        overflow: 'hidden',
        '&:hover': {
          transform: 'translateY(-6px)',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.12)',
          borderColor: '#3b82f6',
        }
      }}
    >
      <CardContent sx={{ p: 0 }}>
        {/* Header Section */}
        <Box 
          sx={{
            background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)',
            p: 3,
            color: 'white',
            position: 'relative',
            '&::after': {
              content: '""',
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              height: '4px',
              background: 'linear-gradient(90deg, #60a5fa, #3b82f6, #1d4ed8)',
            }
          }}
        >
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="h5" sx={{ 
                fontWeight: 700,
                mb: 0.5,
                color: 'white'
              }}>
                Check-ins Overview
              </Typography>
              <Typography variant="body2" sx={{ 
                color: 'rgba(255, 255, 255, 0.8)',
                fontSize: '0.875rem'
              }}>
                Real-time facility usage analytics
              </Typography>
            </Box>
            <Tooltip title="Refresh Data">
              <IconButton 
                size="small" 
                onClick={(e) => {
                  e.stopPropagation();
                  fetchCheckInStats();
                }}
                disabled={loading}
                sx={{
                  background: 'rgba(255, 255, 255, 0.15)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  color: 'white',
                  '&:hover': {
                    background: 'rgba(255, 255, 255, 0.25)',
                    transform: 'rotate(90deg)',
                  },
                  transition: 'all 0.3s ease',
                }}
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Main Content */}
        <Box sx={{ p: 3 }}>
          {/* Today's Highlight */}
          <Box 
            sx={{
              textAlign: 'center',
              mb: 4,
              p: 3,
              background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
              borderRadius: 3,
              border: '2px solid #0ea5e9',
              position: 'relative',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '3px',
                background: 'linear-gradient(90deg, #0ea5e9, #0284c7, #0369a1)',
              }
            }}
          >
            <Typography variant="h2" sx={{ 
              fontWeight: 800,
              color: '#0369a1',
              mb: 1,
              fontSize: '3.5rem',
              textShadow: '0 2px 4px rgba(3, 105, 161, 0.1)'
            }}>
              {stats.today}
            </Typography>
            <Typography variant="h6" sx={{ 
              color: '#0284c7',
              fontWeight: 600,
              mb: 1,
              textTransform: 'uppercase',
              letterSpacing: '1px'
            }}>
              Today's Check-ins
            </Typography>
            <Typography variant="body2" sx={{ 
              color: '#0369a1',
              fontWeight: 500,
              opacity: 0.8
            }}>
              {format(new Date(), 'EEEE, MMMM do, yyyy')}
            </Typography>
          </Box>

          {/* Period Stats Grid */}
          <Box 
            display="grid" 
            gridTemplateColumns="1fr 1fr" 
            gap={2} 
            mb={4}
          >
            <Box 
              onClick={handleWeeklyClick}
              sx={{
                p: 3,
                background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                borderRadius: 3,
                border: '2px solid #f59e0b',
                cursor: 'pointer',
                textAlign: 'center',
                transition: 'all 0.3s ease',
                position: 'relative',
                overflow: 'hidden',
                '&:hover': {
                  transform: 'translateY(-4px) scale(1.02)',
                  boxShadow: '0 12px 32px rgba(245, 158, 11, 0.25)',
                  borderColor: '#d97706',
                },
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: '3px',
                  background: 'linear-gradient(90deg, #f59e0b, #d97706, #b45309)',
                }
              }}
            >
              <Typography variant="h3" sx={{ 
                fontWeight: 800,
                color: '#b45309',
                mb: 1,
                fontSize: '2.5rem'
              }}>
                {stats.week}
              </Typography>
              <Typography variant="subtitle1" sx={{ 
                color: '#d97706',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                This Week
              </Typography>
            </Box>

            <Box 
              onClick={handleMonthlyClick}
              sx={{
                p: 3,
                background: 'linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)',
                borderRadius: 3,
                border: '2px solid #10b981',
                cursor: 'pointer',
                textAlign: 'center',
                transition: 'all 0.3s ease',
                position: 'relative',
                overflow: 'hidden',
                '&:hover': {
                  transform: 'translateY(-4px) scale(1.02)',
                  boxShadow: '0 12px 32px rgba(16, 185, 129, 0.25)',
                  borderColor: '#059669',
                },
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: '3px',
                  background: 'linear-gradient(90deg, #10b981, #059669, #047857)',
                }
              }}
            >
              <Typography variant="h3" sx={{ 
                fontWeight: 800,
                color: '#047857',
                mb: 1,
                fontSize: '2.5rem'
              }}>
                {stats.month}
              </Typography>
              <Typography variant="subtitle1" sx={{ 
                color: '#059669',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                This Month
              </Typography>
            </Box>
          </Box>

          {/* Status Breakdown */}
          {stats.byStatus && stats.byStatus.length > 0 && (
            <Box>
              <Typography variant="h6" sx={{
                color: '#1e293b',
                fontWeight: 700,
                mb: 3,
                textAlign: 'center',
                position: 'relative',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  bottom: '-8px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: '60px',
                  height: '3px',
                  background: 'linear-gradient(90deg, #3b82f6, #1d4ed8)',
                  borderRadius: '2px',
                }
              }}>
                Today's Status Details
              </Typography>
              <Box display="grid" gap={2}>
                {stats.byStatus.map((statusItem) => (
                  <Box 
                    key={statusItem._id} 
                    onClick={handleStatusClick(statusItem._id)}
                    sx={{
                      p: 3,
                      background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                      borderRadius: 3,
                      border: '2px solid #e2e8f0',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      position: 'relative',
                      overflow: 'hidden',
                      '&:hover': {
                        transform: 'translateX(8px)',
                        borderColor: '#3b82f6',
                        boxShadow: '0 8px 25px rgba(59, 130, 246, 0.15)',
                        background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
                      },
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        left: 0,
                        top: 0,
                        bottom: 0,
                        width: '4px',
                        background: statusItem._id === 'success' ? '#10b981' : 
                                   statusItem._id === 'contract-expired' ? '#ef4444' :
                                   statusItem._id === 'payment-overdue' ? '#f59e0b' : '#6b7280',
                        transition: 'width 0.3s ease',
                      },
                      '&:hover::before': {
                        width: '8px',
                      }
                    }}
                  >
                    <Box display="flex" alignItems="center" gap={2}>
                      <Box sx={{
                        p: 1.5,
                        borderRadius: '50%',
                        background: statusItem._id === 'success' ? 'rgba(16, 185, 129, 0.1)' : 
                                   statusItem._id === 'contract-expired' ? 'rgba(239, 68, 68, 0.1)' :
                                   statusItem._id === 'payment-overdue' ? 'rgba(245, 158, 11, 0.1)' : 'rgba(107, 114, 128, 0.1)',
                      }}>
                        {getStatusIcon(statusItem._id)}
                      </Box>
                      <Typography variant="body1" sx={{
                        color: '#1e293b',
                        fontWeight: 600,
                        fontSize: '1.1rem'
                      }}>
                        {getStatusLabel(statusItem._id)}
                      </Typography>
                    </Box>
                    <Box 
                      sx={{
                        background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                        color: 'white',
                        borderRadius: '50%',
                        width: 48,
                        height: 48,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 800,
                        fontSize: '1.2rem',
                        boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)',
                      }}
                    >
                      {statusItem.count}
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          )}

          {/* Action Button */}
          <Box sx={{ mt: 4, textAlign: 'center' }}>
            <Box 
              component="button"
              onClick={() => navigate('/daily-checkins')}
              sx={{
                width: '100%',
                p: 2.5,
                background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                border: 'none',
                borderRadius: 3,
                color: 'white',
                fontWeight: 700,
                fontSize: '1.1rem',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                textTransform: 'uppercase',
                letterSpacing: '1px',
                boxShadow: '0 4px 15px rgba(59, 130, 246, 0.3)',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 25px rgba(59, 130, 246, 0.4)',
                  background: 'linear-gradient(135deg, #2563eb 0%, #1e40af 100%)',
                },
              }}
            >
              View All Check-ins
            </Box>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default CheckInsSummaryCard;
